import { Logger } from '@montugroup/utilities';
import { getEnvironment } from './env';
import { DEBOUNCE_TIME, DEFAULT_PLUGINS, SNOWPLOW_COLLECTION_ENDPOINTS } from './snowplow.constants';
import { pluginRegistry } from './types/plugins.types';
import { trackPageViewTypeSafe } from './typesafe-self-describing-event-tracker';
const logger = new Logger('SnowplowUtils');
/**
 * Initializes a Snowplow tracker and enables tracking features.
 * @param config - Configuration object for Snowplow initialization
 * @param config.tracker - Reference to store the BrowserTracker instance.
 * @param config.appId - Application ID is used to distinguish different applications that are being tracked by the same Snowplow stack
 * @param config.trackerName - Tracker namespace
 * @param config.userId - Unique ID set by business
 * @param config.snowplowEndpointOverride - Optional custom endpoint for Snowplowdd
 * @param config.plugins - Optional plugins to add to the tracker
 *
 */
export const initializeSnowplow = async (config) => {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        const { tracker, appId, trackerName, userId, snowplowEndpointOverride, pluginsOverride } = config;
        const plugins = pluginsOverride ? { ...DEFAULT_PLUGINS, ...pluginsOverride } : DEFAULT_PLUGINS;
        await createTracker({ tracker, trackerName, appId, userId, snowplowEndpointOverride, plugins });
        await configurePlugins({ plugins });
    }
    catch (error) {
        console.error('Error initializing Snowplow tracker', error);
    }
};
let newTracker;
let setUserId;
export const createTracker = async (config) => {
    const { tracker, trackerName, appId, userId, snowplowEndpointOverride, plugins } = config;
    const snowplowEndpoint = snowplowEndpointOverride ?? SNOWPLOW_COLLECTION_ENDPOINTS[getEnvironment()];
    const enabledPluginEntries = Object.entries(pluginRegistry).filter(([name]) => {
        const pluginName = name;
        return plugins?.[pluginName]?.enabled !== false;
    });
    const pluginPromises = enabledPluginEntries.map(([_, plugin]) => plugin.plugin());
    const loadedPlugins = await Promise.all(pluginPromises);
    // Filter out any null results (e.g., plugins that failed to load or aren't for browser)
    const pluginsToAdd = loadedPlugins.filter((p) => p !== null);
    if (!newTracker) {
        newTracker = (await import('@snowplow/browser-tracker')).newTracker;
    }
    tracker.current = newTracker(trackerName, snowplowEndpoint, {
        appId,
        plugins: pluginsToAdd,
    });
    if (userId) {
        if (!setUserId) {
            setUserId = (await import('@snowplow/browser-tracker')).setUserId;
        }
        setUserId(userId);
    }
    return tracker.current;
};
export const configurePlugins = async (config) => {
    if (typeof window === 'undefined' || !config.plugins) {
        return;
    }
    const enablePromises = Object.entries(config.plugins).map(async ([key, pluginConfig]) => {
        const pluginName = key;
        const pluginRegistration = pluginRegistry[pluginName];
        if (pluginConfig?.enabled && 'enable' in pluginRegistration && typeof pluginRegistration.enable === 'function') {
            await pluginRegistration.enable(pluginConfig.config);
        }
    });
    await Promise.all(enablePromises);
};
/**
 * Tracks pageviews on route changes with debounce protection.
 * @param lastTrackedTime - Reference to store the last tracked pageview timestamp.
 *
 * Debounce protection:
 * Prevents tracking multiple pageviews in quick succession by enforcing a minimum
 * time interval {@link DEBOUNCE_TIME} between tracked pageviews. This is useful for
 * handling rapid navigation or route changes without generating duplicate data.
 */
export const trackPageViewWithDebounce = async (trackerName, lastTrackedTime, pageViewContext) => {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        const currentTime = Date.now();
        if (currentTime - lastTrackedTime.current > DEBOUNCE_TIME) {
            lastTrackedTime.current = currentTime;
            await trackPageViewTypeSafe({
                trackers: [trackerName],
                context: pageViewContext,
            });
        }
        else {
            return;
        }
    }
    catch (error) {
        console.error('Error tracking pageview', error);
    }
};
/*
 * Function to extract the Snowplow Domain User Information from the first-party cookie set by the Snowplow JavaScript Tracker
 *
 * @param string cookieName (optional) The value used for "cookieName" in the tracker constructor argmap
 * (leave blank if you did not set a custom cookie name)
 * https://docs.snowplow.io/docs/sources/trackers/javascript-trackers/web-tracker/cookies-and-local-storage/getting-cookie-values/
 * @return string or bool The ID string if the cookie exists or false if the cookie has not been set yet
 */
export function getSnowplowDuid(cookieName) {
    if (typeof window === 'undefined') {
        return false;
    }
    const effectiveCookieName = cookieName || '_sp_';
    const matcher = new RegExp(effectiveCookieName + 'id\\.[a-f0-9]+=([^;]+);?');
    const match = document.cookie.match(matcher);
    if (match && match[1]) {
        const split = match[1].split('.');
        return {
            domain_userid: split[0],
            domain_sessionidx: split[2],
            domain_sessionid: split[5],
        };
    }
    return false;
}
/**
 * Decorates a URL with Snowplow cross-domain tracking parameter (_sp).
 * This is used for programmatic navigation (e.g., window.location.href) where
 * Snowplow's automatic link decoration doesn't apply (that only works on links).
 *
 * Uses the extended cross-domain format to track users across different apps and platforms.
 * The _sp parameter contains domain user ID, timestamp, session ID, business user ID,
 * source app ID, platform, and navigation reason.
 *
 * @param url - The URL to decorate with Snowplow tracking parameter
 * @param options - Optional configuration object
 * @param options.cookieName - Optional custom cookie name (defaults to '_sp_')
 * @param options.subjectUserId - Optional business user ID
 * @param options.sourceId - Optional source application ID
 * @param options.platform - Optional platform identifier (defaults to 'web')
 * @param options.reason - Optional reason for navigation
 * @returns The decorated URL with _sp parameter, or original URL if decoration fails
 *
 * @example
 * const decoratedUrl = decorateUrlWithSnowplow('https://example.com/path?foo=bar', {
 *   subjectUserId: 'user123',
 *   sourceId: 'pms',
 *   platform: 'web',
 *   reason: 'navigation'
 * });
 * // Returns: 'https://example.com/path?foo=bar&_sp={domainUserId}.{timestamp}.{sessionId}.{subjectUserId}.{sourceId}.{platform}.{reason}'
 *
 */
export function decorateUrlWithSnowplow(url, options) {
    if (typeof window === 'undefined') {
        logger.warn('decorateUrlWithSnowplow: window is undefined, returning original URL');
        return url;
    }
    try {
        const snowplowIds = getSnowplowDuid(options?.cookieName);
        if (!snowplowIds) {
            logger.warn('decorateUrlWithSnowplow: No Snowplow domain user ID found, returning original URL');
            return url;
        }
        const timestamp = Date.now();
        const unknown = 'unknown';
        // Build extended cross-domain format: {domainUserId}.{timestamp}.{sessionId}.{subjectUserId}.{sourceId}.{platform}.{reason}
        const spParam = [
            snowplowIds.domain_userid,
            timestamp,
            snowplowIds.domain_sessionid || '',
            options?.subjectUserId || unknown,
            options?.sourceId || unknown,
            options?.platform || 'web',
            options?.reason || unknown,
        ].join('.');
        // Parse the URL to safely add the _sp parameter
        const urlObj = new URL(url);
        urlObj.searchParams.set('_sp', spParam);
        return urlObj.toString();
    }
    catch (error) {
        logger.error('decorateUrlWithSnowplow: Error decorating URL', error);
        return url;
    }
}
/**
 * Builds a context ID by combining a prefix with one or more string parts.
 * This is the standard format for all analytics `context_id` values in PMS.
 *
 * Use underscores (`_`) to separate high-level scopes (e.g. `scheduled_consult_tab`)
 * Use hyphens (`-`) to separate nested parts like sections or components
 *    (e.g. `scheduled_consult_tab-prescriptions-mock_section`)
 *
 * This format ensures consistent, structured tracking across tabs, sections, and components.
 *
 * @param prefix - The top-level context (e.g. "scheduled_consult_tab")
 * @param parts - Additional parts to append (e.g. "prescriptions", "mock_section")
 *
 * @returns A hyphenated context ID string following PMS analytics standards.
 *
 * @example
 * prefixContextIdChain("scheduled_consult_tab")
 * // → "scheduled_consult_tab"
 *
 * prefixContextIdChain("scheduled_consult_tab", "prescriptions", "mock_section")
 * // → "scheduled_consult_tab-prescriptions-mock_section"
 *
 * prefixContextIdChain("scheduled_consult_tab", "scheduled_consult_tab-prescriptions")
 * // → "scheduled_consult_tab-prescriptions"
 */
export const buildContextId = (prefix, ...parts) => {
    const filteredParts = parts.filter((part) => part.trim() !== '');
    const suffix = filteredParts.join('-');
    if (suffix.startsWith(prefix)) {
        return suffix;
    }
    return suffix ? `${prefix}-${suffix}` : prefix;
};
//# sourceMappingURL=data:application/json;base64,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